import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { isFutureCalendarDateInTimezone, membershipBillingStartDate } from "@/lib/billing/membership-billing-start";
import createId from "@/lib/create-id";
import { ItemType } from "@/types/enums/item-type";
import { MembershipShopBillingStart } from "@/types/enums/membership";
import { MembershipShopCartLine, ShopItem } from "@/types/shop";
import { tenantQueryOptions } from "@/query/tenant";
const makeLineId = (): string => createId("cl");
export const isMembershipBillingStartFuture = (billingStart: MembershipShopBillingStart | null | undefined, timezone: string | null | undefined, now = new Date()): boolean => {
  if (!billingStart || billingStart === MembershipShopBillingStart.ENROLLMENT_DAY) {
    return false;
  }
  if (!timezone) return true;
  const startDate = membershipBillingStartDate(billingStart, timezone, now);
  return isFutureCalendarDateInTimezone(startDate, timezone, now);
};
export const membershipFrequencyLabel = (billingFrequency: string): string => billingFrequency.toLowerCase().replaceAll("_", " ");
export const useMembershipBillingTerms = item => {
  const $ = _c(11);
  const tenant = useParams()?.tenant;
  let t0;
  if ($[0] !== tenant) {
    t0 = tenantQueryOptions.public(tenant);
    $[0] = tenant;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: publicTenant
  } = useQuery(t0);
  const t1 = item?.shopBillingStart;
  const t2 = publicTenant?.defaultTimezone;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = isMembershipBillingStartFuture(t1, t2);
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasFutureBillingStart = t3;
  let t4;
  if ($[5] !== publicTenant?.enabledModules) {
    t4 = publicTenant?.enabledModules?.includes("MEMBERSHIP_RENEWAL_CONTROL");
    $[5] = publicTenant?.enabledModules;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const renewalControlEnabled = t4 === true;
  const t5 = !renewalControlEnabled || item?.autoRenew !== false;
  const t6 = hasFutureBillingStart ? "Due at first billing" : "Due now";
  let t7;
  if ($[7] !== hasFutureBillingStart || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      effectivelyRenews: t5,
      hasFutureBillingStart,
      chargeLabel: t6
    };
    $[7] = hasFutureBillingStart;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
export const membershipBillingStartLabel = (billingStart?: MembershipShopBillingStart | null): string => {
  if (billingStart === MembershipShopBillingStart.FIRST_OF_MONTH) {
    return "Billing starts on the 1st";
  }
  if (billingStart === MembershipShopBillingStart.FIFTEENTH_OF_MONTH) {
    return "Billing starts on the 15th";
  }
  return "Starts after checkout";
};
export const lineFromMembership = (item: ShopItem): MembershipShopCartLine => ({
  kind: "membership",
  lineId: makeLineId(),
  itemType: ItemType.MEMBERSHIP,
  itemId: item.id,
  unitPriceCents: item.price,
  expectedUnitPriceCents: item.price,
  quantity: 1,
  form: item.form ? {
    id: item.form.id,
    name: item.form.name,
    pathName: item.form.pathName,
    completed: Boolean(item.form.completed)
  } : null,
  snapshot: {
    name: item.name,
    description: item.description,
    imageUrl: item.imageUrl,
    category: item.category,
    membership: {
      setupFee: item.setupFee,
      recurringPrice: item.recurringPrice,
      billingFrequency: item.billingFrequency,
      autoRenew: item.autoRenew,
      shopBillingStart: item.shopBillingStart,
      form: item.form ? {
        id: item.form.id,
        name: item.form.name,
        pathName: item.form.pathName,
        completed: Boolean(item.form.completed)
      } : null
    }
  }
});